import { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { planService, Plan } from '../../../services/plan.service';
import { tenantPlanService } from '../../../services/tenantPlan.service';
import { toast } from 'react-hot-toast';

export default function PlanSelection() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const data = await planService.getActivePlansForPurchase();
      setPlans(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planId: string) => {
    try {
      setPurchasing(planId);
      const result = await tenantPlanService.purchasePlan({ planId });
      
      if (result.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        toast.success('Plan purchased successfully');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to purchase plan');
      setPurchasing(null);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getDurationLabel = (duration: string) => {
    const labels: Record<string, string> = {
      monthly: 'month',
      quarterly: 'quarter',
      yearly: 'year',
      lifetime: 'lifetime',
    };
    return labels[duration] || duration;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900">Choose Your Plan</h1>
        <p className="mt-4 text-xl text-gray-600">
          Select the perfect plan for your accounting practice
        </p>
      </div>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No plans available at this time</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {plans.map((plan, index) => {
            const isPopular = index === 1; // Middle plan is popular
            
            return (
              <div
                key={plan._id}
                className={`relative bg-white rounded-2xl shadow-xl ${
                  isPopular ? 'border-4 border-blue-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {isPopular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <span className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-blue-500 text-white">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-gray-900">{plan.displayName}</h3>
                  <p className="mt-4 text-gray-600">{plan.description}</p>

                  {/* Pricing */}
                  <div className="mt-6">
                    <div className="flex items-baseline">
                      <span className="text-5xl font-extrabold text-gray-900">
                        {formatCurrency(plan.cost, plan.currency)}
                      </span>
                      <span className="ml-3 text-xl text-gray-600">
                        / {getDurationLabel(plan.duration)}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="mt-8 space-y-4">
                    <li className="flex items-start">
                      <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                      <span className="ml-3 text-gray-700">
                        Up to {plan.maxUsers} users
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                      <span className="ml-3 text-gray-700">
                        Up to {plan.maxClients} clients
                      </span>
                    </li>

                    {/* Custom Features */}
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">{feature.name}</span>
                      </li>
                    ))}

                    {/* Feature Flags */}
                    {plan.featureFlags.eSign && (
                      <li className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">E-Signature</span>
                      </li>
                    )}
                    {plan.featureFlags.payroll && (
                      <li className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">Payroll Management</span>
                      </li>
                    )}
                    {plan.featureFlags.biometricImport && (
                      <li className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">Biometric Import</span>
                      </li>
                    )}
                    {plan.featureFlags.advancedReporting && (
                      <li className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">Advanced Reporting</span>
                      </li>
                    )}
                    {plan.featureFlags.apiAccess && (
                      <li className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">API Access</span>
                      </li>
                    )}
                    {plan.featureFlags.prioritySupport && (
                      <li className="flex items-start">
                        <CheckIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                        <span className="ml-3 text-gray-700">Priority Support</span>
                      </li>
                    )}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handlePurchase(plan._id)}
                    disabled={purchasing === plan._id}
                    className={`mt-8 w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                      isPopular
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-900 hover:bg-gray-800'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {purchasing === plan._id ? 'Processing...' : 'Get Started'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Additional Info */}
      <div className="mt-16 text-center">
        <p className="text-gray-600">
          All plans include 14-day free trial. No credit card required.
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Need help choosing? <a href="/contact" className="text-blue-600 hover:text-blue-700">Contact us</a>
        </p>
      </div>
    </div>
  );
}
